/**
 * Server-Sent Events (SSE) client implementation
 */

import type { SSEStream, ClientConfig } from './types';

/**
 * Create SSE stream
 */
export function createSSEStream<T = any>(path: string, config: ClientConfig): SSEStream<T> {
  const url = `${config.baseURL}${path}`;

  // Create async generator for SSE stream
  return (async function* () {
    try {
      // Create fetch options with token if available
      const headers: Record<string, string> = {
        Accept: 'text/event-stream',
      };

      if (config.getToken) {
        const token = config.getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      // Use ofetch to create stream
      const response = await fetch(url, {
        headers,
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          // Skip empty lines and comments
          if (!line.trim() || line.startsWith(':')) {
            continue;
          }

          // Parse SSE format: "data: {...}"
          if (line.startsWith('data: ')) {
            const data = line.slice(6); // Remove "data: " prefix

            try {
              const parsed = JSON.parse(data);
              yield parsed as T;
            } catch (error) {
              console.error('Failed to parse SSE data:', error);
            }
          }
        }
      }
    } catch (error) {
      console.error('SSE stream error:', error);
      throw error;
    }
  })();
}
